import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar,
  FileText,
  ArrowRight,
  Clock,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { isComplexScopingPackage } from "@/domain/intake";

interface LeadContext {
  leadId: string;
  mode?: "call" | "package";
  email: string;
  name: string;
  packageId: string | null;
  timezone: string;
  ndaRequired: boolean;
  emailVerified?: boolean;
  ndaSigned?: boolean;
  summary: string;
}

function getLeadContext(): LeadContext | null {
  try {
    const raw = localStorage.getItem("echopeak-lead-context");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function EngagementChoicePage() {
  const navigate = useNavigate();
  const [leadContext, setLeadContext] = useState<LeadContext | null>(null);

  useEffect(() => {
    const ctx = getLeadContext();
    if (!ctx || !ctx.emailVerified) {
      navigate("/verify/email");
      return;
    }
    if (ctx.ndaRequired && !ctx.ndaSigned && ctx.mode !== "call") {
      navigate("/verify/nda");
      return;
    }
    setLeadContext(ctx);
  }, [navigate]);

  const handleChooseCall = () => {
    if (!leadContext) return;
    localStorage.setItem(
      "echopeak-lead-context",
      JSON.stringify({ ...leadContext, engagementPath: "call" }),
    );
    navigate("/schedule");
  };

  const handleChooseQuestionnaire = () => {
    if (!leadContext) return;
    localStorage.setItem(
      "echopeak-lead-context",
      JSON.stringify({ ...leadContext, engagementPath: "questionnaire" }),
    );
    navigate("/questionnaire");
  };

  if (!leadContext) return null;

  const showCallRecommendation = isComplexScopingPackage(leadContext.packageId);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24">
      <div className="absolute inset-0 hero-gradient" />
      <div className="absolute inset-0 grid-background opacity-40" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl w-full relative z-10"
      >
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="h-8 w-8 text-primary" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mb-3">
            How would you like to proceed?
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Choose the path that works best for your schedule. You can switch
            between options at any time.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Card
            className="glass-card border-gradient cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg"
            onClick={handleChooseCall}
          >
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                  <Calendar className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <h2 className="font-display text-xl font-bold">
                    Schedule a Discovery Call
                  </h2>
                  {showCallRecommendation && (
                    <Badge variant="outline" className="text-xs mt-1 text-blue-400 border-blue-400/30">
                      Recommended for this package
                    </Badge>
                  )}
                </div>
              </div>

              <p className="text-muted-foreground text-sm mb-6">
                Have a real-time conversation with a senior engineer. Best for
                complex scoping or if you prefer to talk through your needs.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>30-45 minutes via Zoom</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>Pick a time that works for you</span>
                </div>
              </div>

              <Button className="w-full" size="lg">
                Schedule a Call
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          <Card
            className="glass-card border-gradient cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg"
            onClick={handleChooseQuestionnaire}
          >
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                  <FileText className="h-6 w-6 text-emerald-400" />
                </div>
                <div>
                  <h2 className="font-display text-xl font-bold">
                    Tell Us About Your Project
                  </h2>
                </div>
              </div>

              <p className="text-muted-foreground text-sm mb-6">
                Answer a few targeted questions about your project. Our team
                reviews within 1-2 business days and follows up.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>10-15 minutes, at your own pace</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span>Questions tailored to your package</span>
                </div>
              </div>

              <Button variant="outline" className="w-full" size="lg">
                Start Questionnaire
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-6">
          Verified as {leadContext.name} ({leadContext.email})
        </p>
      </motion.div>
    </div>
  );
}
